<!--
  StorePricingGrid.vue -- the store editor's Pricing grid.

  Design source of truth: Phil's prototype at
  planning/design-handoffs/handoff_2026-07-18_from-phil/
  threadflow_store_pricing_poc.html. Layout, copy, caps and defaults are
  taken from that file rather than invented.

  Locked decisions (Jake, 2026-08-25) this surface implements:

    2. ONE stored value + a mode flag. Switching mode CONVERTS in place,
       so the conversion is asked of the SERVER (which owns the math and
       the rounding rule) rather than duplicated in JS -- a second
       implementation of u = m/(1-m) here is exactly the kind of drift
       docs/design-philosophy.md calls a model leak.
    3. This page sets WHO PAYS the platform fee, never the RATE. And the
       word is "shopper", never "customer": the team says "customer" to
       mean CLIENT, so it is ambiguous in this product. This is the one
       place we deliberately depart from the prototype, which labels the
       row "CUSTOMER PAYS".
    5. The -2% steps are a DEFAULT, not a constraint -- every cell is
       independently editable.
    6. The decoration column is what the SHOPPER IS CHARGED for
       decoration, NOT the decorator payout.

  Only the selected mode's columns render (the prototype toggles a
  `hidden-col` class; we render conditionally instead, so the inactive
  mode's inputs are ABSENT rather than merely hidden -- a hidden input is
  still typeable and would let a distributor edit the wrong mode).

  Caps are shown here as rules text and as each input's `max`, but that is
  a hint only: enforcement is server-side and a rejection is surfaced
  verbatim to the user.

  Self-contained persistence: own GET + PATCH against
  /api/stores/<id>/pricing-grid/ (not part of campaign autosave), same
  pattern as StoreAllowancePolicy.vue.
-->
<template>
  <EditorCard
    title="Store Pricing"
    description="Choose how you want to price blank goods and decoration."
  >
    <div class="pricing-grid" data-testid="pricing-grid-root">
      <div v-if="loading" data-testid="pricing-grid-loading">Loading...</div>

      <template v-else>
        <!--
          Which products these tables price (2026-09-29). A product is on
          "Store pricing" (priced here) or "Fixed price" (its own per-size
          prices, set in the product editor, which these tables never touch).
        -->
        <p class="pg-scope" data-testid="pricing-grid-scope">
          These tables price every product set to <strong>Store pricing</strong>.
          Products set to <strong>Fixed price</strong> keep the prices you
          entered for them and are not affected.
        </p>

        <!-- Pricing strategy ------------------------------------------->
        <div class="pg-strategy">
          <button
            type="button"
            class="pg-choice"
            :class="{ 'pg-choice--active': mode === 'margin' }"
            data-testid="pricing-grid-mode-margin"
            @click="selectMode('margin')"
          >
            <span class="pg-choice__title">MARGIN</span>
            <span class="pg-choice__copy"
              >Margin is a percentage of the selling price.</span
            >
            <span
              v-if="mode === 'margin'"
              data-testid="pricing-grid-mode-margin-active"
              class="pg-choice__flag"
            />
          </button>

          <button
            type="button"
            class="pg-choice"
            :class="{ 'pg-choice--active': mode === 'markup' }"
            data-testid="pricing-grid-mode-markup"
            @click="selectMode('markup')"
          >
            <span class="pg-choice__title">MARKUP</span>
            <span class="pg-choice__copy"
              >Markup is a percentage of your cost.</span
            >
            <span
              v-if="mode === 'markup'"
              data-testid="pricing-grid-mode-markup-active"
              class="pg-choice__flag"
            />
          </button>

          <aside class="pg-info">
            <h4>How it works</h4>
            <p>
              When you select either Margin or Markup, only the columns for
              your selection are shown. You can change this at any time --
              your percentages are converted so prices stay the same.
            </p>
          </aside>
        </div>

        <!--
          An inherited base margin above the cap. The seeded first row is
          clamped so the page stays usable, but a silently clamped number
          is a lie by omission -- so the server's explanation (which names
          both the inherited value and the cap) is shown here. The page
          stays fully editable: blocking a bad SAVE must not brick editing
          for exactly the stores that need fixing.
        -->
        <p
          v-if="baseMarginWarning"
          class="pg-warning"
          data-testid="pricing-grid-base-margin-warning"
        >
          {{ baseMarginWarning }}
        </p>

        <!-- Pricing table ---------------------------------------------->
        <div class="pg-layout">
          <div class="pg-table-wrap">
            <table class="pg-table">
              <thead>
                <tr>
                  <th>QUANTITY BREAKS</th>
                  <th>{{ modeLabel }} % BLANK GOODS</th>
                  <th>{{ modeLabel }} % DECORATION</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(label, i) in quantityBreaks"
                  :key="label"
                  data-testid="pricing-grid-row"
                >
                  <td class="pg-break">{{ label }}</td>
                  <td>
                    <!--
                      type="text" + inputmode="decimal", NOT type="number":
                      a number input normalises its own value on every
                      keystroke, so a half-typed "40." is silently rewritten
                      and a decimal becomes impossible to enter. The cost is
                      that the browser's min/max/step no longer apply, so the
                      cap travels as an ARIA range instead (`max` is inert on
                      a text input but kept as the cap's declared carrier).
                      The cap was always a HINT -- enforcement is server-side
                      and a rejection is surfaced verbatim -- so nothing that
                      was enforced has been lost.
                    -->
                    <div class="pg-input-shell">
                      <input
                        class="pg-input"
                        type="text"
                        inputmode="decimal"
                        role="spinbutton"
                        aria-valuemin="0"
                        :aria-valuemax="blankCap"
                        :max="blankCap"
                        :value="blankDisplay[i]"
                        :aria-label="`${modeLabel} percent on blank goods for ${label}`"
                        data-testid="pricing-grid-blank-input"
                        @input="onCell('blankGoods', i, $event)"
                        @blur="onBlur('blankGoods', i)"
                      />
                      <span
                        class="pg-input-affix"
                        aria-hidden="true"
                        data-testid="pricing-grid-percent-affix"
                        >%</span
                      >
                    </div>
                  </td>
                  <td>
                    <div class="pg-input-shell">
                      <input
                        class="pg-input"
                        type="text"
                        inputmode="decimal"
                        role="spinbutton"
                        aria-valuemin="0"
                        :aria-valuemax="decorationCap"
                        :max="decorationCap"
                        :value="decorationDisplay[i]"
                        :aria-label="`${modeLabel} percent on decoration for ${label}`"
                        data-testid="pricing-grid-decoration-input"
                        @input="onCell('decoration', i, $event)"
                        @blur="onBlur('decoration', i)"
                      />
                      <span
                        class="pg-input-affix"
                        aria-hidden="true"
                        data-testid="pricing-grid-percent-affix"
                        >%</span
                      >
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pg-rules">
            <aside
              class="pg-rule pg-rule--margin"
              data-testid="pricing-grid-margin-rules"
            >
              <h4>MARGIN RULES</h4>
              <ul>
                <li>Margin on blank goods maximum = {{ caps.margin.blank_goods }}%</li>
                <li>Margin on decoration maximum = {{ caps.margin.decoration }}%</li>
              </ul>
            </aside>
            <aside
              class="pg-rule pg-rule--markup"
              data-testid="pricing-grid-markup-rules"
            >
              <h4>MARKUP RULES</h4>
              <ul>
                <li>Markup on blank goods maximum = {{ caps.markup.blank_goods }}%</li>
                <li>Markup on decoration maximum = {{ caps.markup.decoration }}%</li>
              </ul>
            </aside>
          </div>
        </div>

        <!--
          The decoration column is LIVE: its percentage is applied to each
          item's decoration cost and charged to the shopper (2026-09-29).
        -->
        <p class="pg-note" data-testid="pricing-grid-decoration-note">
          Decoration percentages are applied to each item's decoration cost
          and added to the price the shopper pays. They are not what the
          decorator is paid.
        </p>

        <div class="pg-actions">
          <v-btn
            color="primary"
            rounded="pill"
            :loading="saving"
            data-testid="pricing-grid-save"
            @click="save"
          >
            Save pricing
          </v-btn>
          <span
            v-if="justSaved"
            class="pg-saved"
            data-testid="pricing-grid-saved"
          >
            Settings saved.
          </span>
        </div>

        <p v-if="errorMsg" class="pg-error" data-testid="pricing-grid-error">
          {{ errorMsg }}
        </p>

        <!-- Platform fee bearer ---------------------------------------->
        <section class="pg-fee" data-testid="pricing-grid-fee-section">
          <p class="pg-fee__title" data-testid="pricing-grid-fee-title">
            {{ feeTitle }}
          </p>
          <PlatformFeeAllowanceWarning :show="showFeeWarning" />

          <button
            type="button"
            class="pg-fee__row"
            :class="{ 'pg-fee__row--active': feeBearer === 'distributor' }"
            data-testid="pricing-grid-fee-distributor"
            @click="selectBearer('distributor')"
          >
            <span class="pg-fee__name">DISTRIBUTOR PAYS</span>
            <!-- OPTION A COPY (Jake, 2026-09-11). This option decides the
                 shopper's PRICE, never whether the platform fee is charged:
                 the fee is always collected as a Stripe application fee on
                 the order, so absorbing it REDUCES THE DISTRIBUTOR'S NET
                 rather than waiving it. The previous copy said the fee came
                 out of gross profit -- true, but it promised the deduction
                 would "appear as a line item in the job gross profit report",
                 which named a report rather than the money, and read as
                 deferred/optional. Say plainly that the money is deducted.

                 DELIBERATELY NOT THE WORD "PAYOUT", even though that is
                 literally what Stripe reduces. On this surface "payout" is
                 reserved vocabulary for the DECORATOR payout (decision 6), and
                 store-pricing-grid.spec.js asserts the panel never contains
                 it -- using it here would make the pricing grid read as though
                 it set decorator settlement. "Net" carries the same meaning
                 with no collision.

                 Asserted by tests/ui-scenarios/dm-store-pricing-fee-bearer-copy.yaml. -->
            <span class="pg-fee__copy" data-testid="pricing-grid-fee-distributor-copy">
              The platform fee is deducted from each order, so your net is
              reduced by the fee. Shown as a deduction in the job gross profit
              report.
            </span>
            <span
              v-if="feeBearer === 'distributor'"
              data-testid="pricing-grid-fee-distributor-active"
              class="pg-fee__flag"
            />
          </button>

          <button
            type="button"
            class="pg-fee__row"
            :class="{ 'pg-fee__row--active': feeBearer === 'shopper' }"
            data-testid="pricing-grid-fee-shopper"
            @click="selectBearer('shopper')"
          >
            <span class="pg-fee__name">SHOPPER PAYS</span>
            <span class="pg-fee__copy" data-testid="pricing-grid-fee-shopper-copy">
              By adding the platform fee to the sell price of each item. The
              shopper will not see the platform fee.
            </span>
            <span
              v-if="feeBearer === 'shopper'"
              data-testid="pricing-grid-fee-shopper-active"
              class="pg-fee__flag"
            />
          </button>
        </section>

        <p class="pg-footer">
          You can update these settings at any time. Changes will apply to new
          quotes and orders.
        </p>
      </template>
    </div>
  </EditorCard>
</template>

<script>
import axios from "@/axios";
import EditorCard from "./components/EditorCard.vue";
import PlatformFeeAllowanceWarning from "./components/PlatformFeeAllowanceWarning.vue";

// Shown only until the first load resolves; the server is authoritative
// for the real caps and breaks (it serves both) so nothing here is a
// second source of truth for them.
const PLACEHOLDER_CAPS = {
  margin: { blank_goods: 50, decoration: 25 },
  markup: { blank_goods: 100, decoration: 50 },
};

/**
 * The DISPLAY form of a percentage: two decimal places, no "%" (the
 * sign is a non-editable CSS affix beside the box, per Phil's
 * prototype's `.input-shell::after`, so it can never be typed into or
 * submitted as part of the value).
 *
 * An empty or unparseable cell stays empty rather than becoming
 * "0.00": inventing a zero for a box the user just cleared would be a
 * silent price change.
 */
function formatPercent(value) {
  if (value === "" || value === null || value === undefined) return "";
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "";
}

/** The display-string field paired with a value field. */
function displayKey(which) {
  return which === "decoration" ? "decorationDisplay" : "blankDisplay";
}

export default {
  name: "StorePricingGrid",

  components: { EditorCard, PlatformFeeAllowanceWarning },

  props: {
    storeId: { type: [Number, String], required: true },
    // Bumped by the store editor when another card on this page changes an
    // input of the platform-fee warning (the Shopper Allowance save), so
    // the one warning re-reads its flag without reloading the grid.
    refreshKey: { type: Number, default: 0 },
  },

  data() {
    return {
      loading: true,
      saving: false,
      justSaved: false,
      errorMsg: "",
      mode: "margin",
      // The AUTHORITATIVE values, at whatever precision the server sent
      // (4dp) or the user typed. Never rounded for display.
      blankGoods: [],
      decoration: [],
      // The display STRINGS the inputs are bound to. Kept separate from
      // the values above so a 2dp display can never become the stored
      // number -- rounding the store would drift the price on every
      // margin<->markup toggle, which is exactly the fixed point the
      // server's conversion is designed to preserve.
      blankDisplay: [],
      decorationDisplay: [],
      quantityBreaks: [],
      caps: PLACEHOLDER_CAPS,
      feeBearer: "shopper",
      // Server-decided (decision #8): shoppers pay the platform fee by card
      // even when an allowance covers the order. Refreshed by every
      // response, so a bearer save updates it.
      feeChargedDespiteAllowance: false,
      // The platform fee rate in basis points, served by the API so the
      // heading never hard-codes a percentage.
      feeBasisPoints: null,
      // The bearer being saved right now, so the warning reacts to the
      // click and not only to the server's answer.
      pendingBearer: null,
      isSeeded: true,
      baseMarginWarning: "",
    };
  },

  computed: {
    // The warning describes a charge only the SHOPPER bearer causes. Hidden
    // the moment another bearer is chosen; restored if that save fails.
    feeTitle() {
      if (this.feeBasisPoints == null) return "Platform fee";
      const pct = (this.feeBasisPoints / 100).toFixed(2);
      return `Platform fee: ${pct}% of the selling price of apparel and decoration (not shipping or tax)`;
    },
    showFeeWarning() {
      const bearer = this.pendingBearer || this.feeBearer;
      return this.feeChargedDespiteAllowance && bearer === "shopper";
    },
    modeLabel() {
      return this.mode === "markup" ? "MARKUP" : "MARGIN";
    },
    blankCap() {
      return this.caps?.[this.mode]?.blank_goods;
    },
    decorationCap() {
      return this.caps?.[this.mode]?.decoration;
    },
  },

  watch: {
    refreshKey() {
      this.refreshFeeWarning();
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    _apply(data) {
      if (!data) return;
      this.mode = data.mode || "margin";
      this.blankGoods = Array.isArray(data.blank_goods)
        ? [...data.blank_goods]
        : [];
      this.decoration = Array.isArray(data.decoration)
        ? [...data.decoration]
        : [];
      // Every server-sourced value (initial load, mode swap, save echo)
      // settles to the 2dp display form.
      this.blankDisplay = this.blankGoods.map(formatPercent);
      this.decorationDisplay = this.decoration.map(formatPercent);
      if (Array.isArray(data.quantity_breaks)) {
        this.quantityBreaks = [...data.quantity_breaks];
      }
      if (data.caps) this.caps = data.caps;
      if (data.platform_fee_bearer) {
        this.feeBearer = data.platform_fee_bearer;
      }
      this.feeChargedDespiteAllowance =
        data.platform_fee_charged_despite_allowance === true;
      if (data.platform_fee_basis_points != null) {
        this.feeBasisPoints = data.platform_fee_basis_points;
      }
      this.isSeeded = !!data.is_seeded;
      this.baseMarginWarning = data.base_margin_warning || "";
    },

    /** Re-read only the platform-fee warning flag; never touches the grid,
     * so an unsaved edit in progress survives. */
    async refreshFeeWarning() {
      try {
        const resp = await axios.get(
          `/api/stores/${this.storeId}/pricing-grid/`,
        );
        this.feeChargedDespiteAllowance =
          resp.data?.platform_fee_charged_despite_allowance === true;
      } catch (e) {
        // Keep the last known answer; the warning is advisory.
      }
    },

    async load() {
      this.loading = true;
      try {
        const resp = await axios.get(
          `/api/stores/${this.storeId}/pricing-grid/`,
        );
        this._apply(resp.data);
      } catch (e) {
        this.errorMsg = this._message(e, "Could not load store pricing.");
      } finally {
        this.loading = false;
      }
    },

    _message(e, fallback) {
      const data = e?.response?.data;
      if (typeof data === "string" && data) return data;
      if (data?.message) return data.message;
      if (data?.error) return data.error;
      return fallback;
    },

    /**
     * Record a keystroke WITHOUT reformatting.
     *
     * The display string is stored verbatim so the field never fights
     * the typist: reformatting on every input would eat the trailing
     * "." of "40." and make a decimal impossible to type. Formatting
     * happens on blur (``onBlur``) instead.
     *
     * The numeric value is parsed alongside at full typed precision. An
     * unparseable intermediate ("", "40.", "-") leaves the value as ""
     * so the save path normalises it rather than storing NaN.
     */
    onCell(which, index, event) {
      const raw = event?.target?.value ?? "";

      const shown = [...this[displayKey(which)]];
      shown[index] = raw;
      this[displayKey(which)] = shown;

      const next = [...this[which]];
      const parsed = Number(raw);
      next[index] = raw === "" || !Number.isFinite(parsed) ? "" : parsed;
      this[which] = next;

      this.justSaved = false;
    },

    /**
     * Settle the cell's DISPLAY to two decimals when the user leaves it.
     * The stored value is untouched -- typing 33.333 shows "33.33" and
     * still saves 33.333.
     */
    onBlur(which, index) {
      const value = this[which][index];
      const shown = [...this[displayKey(which)]];
      shown[index] = formatPercent(value);
      this[displayKey(which)] = shown;
    },

    async selectMode(mode) {
      if (mode === this.mode) return;
      // Ask the SERVER to convert: it owns the conversion math and the
      // rounding rule that makes repeated toggling stable. Re-implementing
      // it here would be a second source of truth.
      this.errorMsg = "";
      this.saving = true;
      try {
        const resp = await axios.patch(
          `/api/stores/${this.storeId}/pricing-grid/`,
          { mode },
        );
        this._apply(resp.data);
      } catch (e) {
        this.errorMsg = this._message(e, "Could not switch pricing mode.");
      } finally {
        this.saving = false;
      }
    },

    async selectBearer(bearer) {
      if (bearer === this.feeBearer) return;
      this.errorMsg = "";
      this.saving = true;
      this.pendingBearer = bearer;
      try {
        const resp = await axios.patch(
          `/api/stores/${this.storeId}/pricing-grid/`,
          { platform_fee_bearer: bearer },
        );
        this._apply(resp.data);
      } catch (e) {
        this.errorMsg = this._message(
          e,
          "Could not update who pays the platform fee.",
        );
      } finally {
        this.pendingBearer = null;
        this.saving = false;
      }
    },

    _numeric(list) {
      return list.map((v) => (v === "" || v === null ? 0 : Number(v)));
    },

    async save() {
      this.errorMsg = "";
      this.saving = true;
      this.justSaved = false;
      try {
        const resp = await axios.patch(
          `/api/stores/${this.storeId}/pricing-grid/`,
          {
            mode: this.mode,
            blank_goods: this._numeric(this.blankGoods),
            decoration: this._numeric(this.decoration),
          },
        );
        this._apply(resp.data);
        this.justSaved = true;
      } catch (e) {
        // A cap rejection arrives as a 400 whose body names the cap.
        // Surface it verbatim -- the server is the enforcement point.
        this.errorMsg = this._message(e, "Could not save store pricing.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Density pass (Jake, 2026-08-26): "the Store Pricing card uses larger
   fonts than expected -- make it tighter". Every value below steps DOWN
   one rung of the shared --tf-sp-* scale in styles/_tokens.css rather
   than inventing a new px value, so the card stays on the same rhythm
   as the rest of the editor while reading denser. Type drops to a 13px
   body / 12px supporting scale, one step under EditorCard's 14px
   description. */
.pricing-grid {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-3, 12px);
  font-size: 13px;
}
.pg-strategy {
  display: grid;
  grid-template-columns: 1fr 1fr 1.4fr;
  gap: var(--tf-sp-3, 12px);
  align-items: stretch;
}
.pg-choice {
  border: 1px solid var(--tf-line, #e2e8f0);
  border-radius: var(--tf-radius-xs, 4px);
  padding: var(--tf-sp-3, 12px);
  text-align: left;
  background: #fff;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-1, 4px);
}
.pg-choice--active {
  border-color: #7aa2ff;
  background: linear-gradient(180deg, #f8fbff 0%, #f1f6ff 100%);
}
.pg-choice__title {
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.3px;
}
.pg-choice__copy {
  color: var(--tf-muted, #64748b);
  font-size: 12px;
  line-height: 1.4;
}
.pg-info {
  border: 1px solid #cfe0ff;
  background: #eff6ff;
  border-radius: var(--tf-radius-xs, 4px);
  padding: var(--tf-sp-3, 12px);
}
.pg-info h4 {
  color: #2563eb;
  margin: 0 0 var(--tf-sp-1, 4px);
  font-size: 13px;
}
.pg-info p {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
}
.pg-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: var(--tf-sp-3, 12px);
  align-items: start;
}
.pg-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--tf-line, #e2e8f0);
  border-radius: var(--tf-radius-xs, 4px);
}
.pg-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}
.pg-table th,
.pg-table td {
  border-right: 1px solid var(--tf-line, #e2e8f0);
  border-bottom: 1px solid var(--tf-line, #e2e8f0);
  padding: var(--tf-sp-1, 4px) var(--tf-sp-2, 8px);
  text-align: center;
}
.pg-table th:last-child,
.pg-table td:last-child {
  border-right: 0;
}
.pg-table tbody tr:last-child td {
  border-bottom: 0;
}
.pg-table th {
  background: #f8fafc;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  line-height: 1.3;
}
.pg-break {
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  width: 120px;
}
/* Percent affix, straight from Phil's prototype (.input-shell +
   ::after content:"%"): the sign is painted BESIDE the value, never
   inside it. A literal "%" in the input's value would be submitted,
   re-parsed and eventually saved -- so the right padding below is what
   reserves room for it. */
.pg-input-shell {
  position: relative;
  display: block;
}
.pg-input {
  width: 100%;
  min-width: 76px;
  padding: var(--tf-sp-1, 4px) 22px var(--tf-sp-1, 4px) var(--tf-sp-2, 8px);
  border: 1px solid #dbe3ef;
  border-radius: var(--tf-radius-xs, 4px);
  text-align: right;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.pg-input:focus {
  border-color: #7aa2ff;
  outline: none;
}
.pg-input-affix {
  position: absolute;
  right: var(--tf-sp-2, 8px);
  top: 50%;
  transform: translateY(-50%);
  color: var(--tf-muted, #64748b);
  font-size: 12px;
  /* Non-editable by construction: it is not an input, and clicks fall
     through to the field behind it. */
  pointer-events: none;
}
.pg-rule {
  border-radius: var(--tf-radius-xs, 4px);
  padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
  margin-bottom: var(--tf-sp-2, 8px);
  border: 1px solid var(--tf-line, #e2e8f0);
}
.pg-rule--margin {
  background: #f0fdf4;
  border-color: #d7f0df;
}
.pg-rule--markup {
  background: #faf5ff;
  border-color: #eadcff;
}
.pg-rule h4 {
  margin: 0 0 var(--tf-sp-1, 4px);
  font-size: 12px;
  letter-spacing: 0.3px;
}
.pg-rule ul {
  margin: 0;
  padding-left: var(--tf-sp-4, 16px);
  font-size: 12px;
  line-height: 1.5;
}
.pg-scope {
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 var(--tf-sp-3, 12px);
}
.pg-note,
.pg-footer {
  color: var(--tf-muted, #64748b);
  font-size: 12px;
  margin: 0;
  line-height: 1.4;
}
.pg-actions {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-2, 8px);
}
.pg-saved {
  color: #15803d;
  font-size: 12px;
}
.pg-warning {
  margin: 0;
  padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
  border: 1px solid #f5d491;
  background: #fffaf0;
  border-radius: var(--tf-radius-xs, 4px);
  font-size: 12px;
  color: #7a4b00;
}
.pg-error {
  color: #b3261e;
  font-size: 12px;
  margin: 0;
}
.pg-fee {
  border-top: 1px solid var(--tf-line, #e2e8f0);
  padding-top: var(--tf-sp-3, 12px);
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-2, 8px);
}
.pg-fee__title {
  font-weight: 700;
  font-size: 13px;
  margin: 0;
}
.pg-fee__row {
  border: 1px solid var(--tf-line, #e2e8f0);
  border-radius: var(--tf-radius-xs, 4px);
  padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
  background: #fff;
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.pg-fee__row--active {
  border-color: #7aa2ff;
  background: #f8fbff;
}
.pg-fee__name {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;
}
.pg-fee__copy {
  color: var(--tf-muted, #64748b);
  font-size: 12px;
  line-height: 1.4;
}
@media (max-width: 1050px) {
  .pg-strategy,
  .pg-layout {
    grid-template-columns: 1fr;
  }
}
</style>
